import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Wrench } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getToken } from '../../services/tokenService';
import { useIsSuperAdmin } from '../DocumentSections/RegenererRecuButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/recus/regeneration/ecarts-dispositif`;
const LIBELLE = { annuel: 'Annuelle', nominative: 'Nominative (chantier)' };


const EcartRow = ({ e, busy, onFix }) => (
  <tr className="border-b" data-testid={`ecart-row-${e.souscription_id}`}>
    <td className="p-2"><div className="font-medium">{e.raison_sociale}</div><div className="text-xs text-slate-500">{e.reference} · {e.devis_id}</div></td>
    <td className="p-2"><Badge className="bg-red-100 text-red-700 font-semibold">{LIBELLE[e.dispositif_actuel] || e.dispositif_actuel}</Badge></td>
    <td className="p-2"><Badge className="bg-emerald-100 text-emerald-700 font-semibold">{LIBELLE[e.dispositif_proposition]}</Badge>{e.chantier && <div className="text-xs text-slate-500 mt-1">{e.chantier}</div>}</td>
    <td className="p-2 text-right">
      <Button size="sm" disabled={busy} onClick={() => onFix(e)} data-testid={`ecart-corriger-${e.souscription_id}`}>
        <Wrench className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} />Corriger
      </Button>
    </td>
  </tr>
);

export const EcartsDispositif = () => {
  const [open, setOpen] = useState(false);
  const [ecarts, setEcarts] = useState(null);
  const [busy, setBusy] = useState(null);
  const superAdmin = useIsSuperAdmin();
  const headers = { Authorization: `Bearer ${getToken()}` };

  const charger = useCallback(async () => {
    setEcarts(null);
    try { setEcarts((await axios.get(API, { headers: { Authorization: `Bearer ${getToken()}` } })).data.ecarts); }
    catch (err) { setEcarts([]); toast.error(err.response?.data?.detail || 'Chargement impossible'); }
  }, []);

  const corriger = async (e) => {
    setBusy(e.souscription_id);
    try {
      const { data } = await axios.post(`${API}/${e.souscription_id}/corriger`, {}, { headers, timeout: 180000 });
      toast.success(`${e.raison_sociale} : dispositif corrigé, ${data.documents.length} document(s) refait(s)`);
      setEcarts((l) => l.filter((x) => x.souscription_id !== e.souscription_id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Correction impossible'); }
    finally { setBusy(null); }
  };

  if (!superAdmin) return null;
  return (
    <>
      <Button variant="outline" className="border-red-300 text-red-700" onClick={() => { setOpen(true); charger(); }} data-testid="ecarts-dispositif-open-btn">
        <AlertTriangle className="h-4 w-4 mr-2" /> Écarts de dispositif
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl" data-testid="ecarts-dispositif-dialog">
          <DialogHeader><DialogTitle>Souscriptions dont le dispositif diffère de la proposition</DialogTitle></DialogHeader>
          <div className="flex justify-end"><Button size="sm" variant="ghost" onClick={charger} data-testid="ecarts-dispositif-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Actualiser</Button></div>
          {ecarts === null ? <p className="text-sm text-slate-500" data-testid="ecarts-loading">Chargement…</p>
            : ecarts.length === 0 ? <p className="text-sm text-emerald-700" data-testid="ecarts-empty">Aucun écart : toutes les souscriptions suivent leur proposition.</p>
            : (
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="p-2">Souscription</th><th className="p-2">Dispositif actuel</th><th className="p-2">Dispositif de la proposition</th><th /></tr></thead>
                  <tbody>{ecarts.map((e) => <EcartRow key={e.souscription_id} e={e} busy={busy === e.souscription_id} onFix={corriger} />)}</tbody>
                </table>
              </div>
            )}
          <p className="text-xs text-slate-500">« Corriger » reprend le dispositif, le chantier et la période de la proposition, puis refait l'attestation, les CP/CG et les reçus (mêmes numéros, sans prévenir le membre).</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
